import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';
import {
  PREQUAL_STATE_OPTIONS,
  residenceFormRules,
} from '@credit-acceptance/shareable-utilities';
import {
  AddressComponentsResult,
  PlacesAutocompleteConfig,
} from '@credit-acceptance/www-api-sdk-fe';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics } from 'adobe-analytics';
import isEqual from 'lodash/isEqual';
import { map, Observable, startWith, takeUntil, tap, zip } from 'rxjs';

import { error_sign, PREQUAL_JSON } from '../../assets/constants';
import { PrequalService } from '../services/prequal.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS } from '../shared/constants/fsevents';
import { ResidenceInfo } from '../shared/models/prequal.types';
import { applyServerFieldErrorsToGroup } from '../summary/caps-validation-field-errors';
import { CrossComponentDateValidationService } from '../validators/cross-component-date-validation.service';
import { SiblingValidationService } from '../validators/sibling-validation.service';
import { residenceAddressContaminationValidator } from './validation/residence-address-contamination.validator';
import { residenceFieldValidator } from './validation/residence-field.validator';
import { residenceMonthResidingValidator } from './validation/residence-month-residing.validator';
import { residenceStreetNumberValidator } from './validation/residence-street-number.validator';
import { ResidenceZipCodeAsyncValidator } from './validation/residence-zip-code.async-validator';

type TSelectOptions = { label: string; value: string };
declare const FS: typeof FullStory;

@Component({
  selector: 'capq-residence',
  templateUrl: './residence.component.html',
  styleUrls: ['./residence.component.scss'],
  standalone: false,
})
export class ResidenceComponent extends Unsubscribe implements OnInit {
  @Output() activeStep: EventEmitter<number> = new EventEmitter<number>();
  @Input() adobeScriptLoaded = false;
  pageViewFired = false;
  autocompleteOptions: PlacesAutocompleteConfig = {
    includedPrimaryTypes: ['street_address', 'premise'],
    regionCodes: ['us'],
  };
  initState = {
    addressLine1: [
      '',
      [
        residenceFieldValidator(),
        residenceAddressContaminationValidator(),
        residenceStreetNumberValidator(),
      ],
    ],
    addressLine2: new FormControl<string | undefined | null>('', [
      residenceFieldValidator(),
      Validators.maxLength(10),
    ]),
    city: ['', residenceFieldValidator()],
    userState: ['', residenceFieldValidator()],
    monthResiding: new FormControl<string | undefined | null>('', [
      residenceFieldValidator(),
      residenceMonthResidingValidator(),
    ]),
    zipCode: [
      '',
      [residenceFieldValidator()],
      this.residenceZipCodeAsyncValidator.validate.bind(
        this.residenceZipCodeAsyncValidator
      ),
    ],
    ownedBy: new FormControl<string | undefined | null>(
      '',
      residenceFieldValidator()
    ),
  };
  residenceForm = this.formBuilder.group(this.initState);

  profanityFlag = false;
  dobMisMatchError = false;
  isWaiting = false;
  futureDateError = false;
  zipCodeErrorFlag = false;
  usStates: TSelectOptions[] = [];
  ownedByList: TSelectOptions[] = [];
  options = {
    types: [],
    componentRestrictions: { country: 'UA' },
  };
  firstName = '';
  personalData: any; // FIXME: this probably can be typed up
  exclamation = error_sign;
  formFieldData: any; // FIXME: this probably can be typed up
  globalText: any; // FIXME: this probably can be typed up
  formIsIncomplete$!: Observable<boolean>;
  isGoogleAutoCompleteWorking = false;
  formStartEventEmitted = false;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private prequalService: PrequalService,
    private readonly residenceZipCodeAsyncValidator: ResidenceZipCodeAsyncValidator,
    private siblingValidationService: SiblingValidationService,
    private cdRef: ChangeDetectorRef,
    private crossComponentDateValidation: CrossComponentDateValidationService
  ) {
    super();

    this.crossComponentDateValidation.registerResidencyValidator(() => {
      this.residenceForm.controls.monthResiding.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.residenceForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.prequalService.setResidenceData({
          city: this.residenceForm.controls.city.value,
          userState: this.residenceForm.controls.userState.value,
          zipCode: this.residenceForm.controls.zipCode.value,
        } as ResidenceInfo);
        if (this.adobeScriptLoaded && !this.pageViewFired) {
          adobeAnalytics.emitEvent('cac-page-view', 'residence', {
            form_design: 'v 1.0',
            section_name: 'residence',
            form_submission_id: '',
            dealer_list: '',
            form_submission_status: '',
            event_type: 'cac-page-view',
          });
          this.pageViewFired = true;
        }
        if (
          this.adobeScriptLoaded &&
          !isEqual(this.initState, this.residenceForm.getRawValue()) &&
          !this.formStartEventEmitted
        ) {
          this.formStartEventEmitted = true;
          adobeAnalytics.emitFormStart('residence');
        }
      });

    this.residenceForm.controls.ownedBy.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (!(typeof FS === 'undefined')) {
          FS.event(FS_EVENTS.RESIDENCE_SECTION_COMPLETE, {
            ownedBy: true,
          });
        }
      });
    this.isGoogleAutoCompleteWorking =
      this.prequalService.getIsGoogleAutoCompleteWorking();

    this.siblingValidationService.validate$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const formKeys = Object.keys(this.residenceForm.controls);
        for (const key of formKeys) {
          this.residenceForm.controls[
            key as keyof typeof residenceFormRules
          ].markAsTouched();
        }
        this.initFormChecks();
        this.cdRef.detectChanges();
        this.siblingValidationService.reportValidation(
          2,
          this.residenceForm.getRawValue(),
          this.residenceForm.valid
        );
      });
    this.initFormChecks();
    this.globalText = PREQUAL_JSON;
    this.formFieldData = PREQUAL_JSON?.residence;
    this.usStates = [...PREQUAL_STATE_OPTIONS];
    this.ownedByList = this.formFieldData?.formsFields?.ownedBy?.options;

    this.prequalService.serverFieldErrors$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((errors) => {
        applyServerFieldErrorsToGroup(errors, 'residence', (name) =>
          this.residenceForm.get(name)
        );
        this.cdRef.detectChanges();
      });
  }

  initFormChecks(): void {
    this.formIsIncomplete$ = this.residenceForm.statusChanges.pipe(
      map((status) => {
        const { addressLine2, ...requiredControls } =
          this.residenceForm.controls;
        return !(status === 'VALID' && !this.isWaiting);
      }),
      startWith(true)
    );
  }

  get f() {
    return this.residenceForm.controls;
  }

  clearNonAlphas(address: string) {
    return address.replace(/[^a-zA-Z0-9 ]/g, '');
  }

  public handleAddressChange(components: AddressComponentsResult) {
    let streetname = '';
    (components ?? []).forEach((component) => {
      if (component.types.includes('street_number')) {
        streetname = this.clearNonAlphas(component.shortText ?? '');
      }
      if (component.types.includes('route')) {
        this.residenceForm.controls.addressLine1.setValue(
          `${streetname ? streetname + ' ' : ''}${this.clearNonAlphas(
            component.longText ?? ''
          )}`
        );
        this.residenceForm.controls.addressLine1.markAsTouched();
      }
      if (component.types.includes('locality')) {
        this.residenceForm.controls.city.setValue(
          this.clearNonAlphas(component.longText ?? '')
        );
        this.residenceForm.controls.city.markAsTouched();
      }
      if (component.types.includes('administrative_area_level_1')) {
        this.residenceForm.controls.userState.setValue(
          component.shortText ?? ''
        );
        this.residenceForm.controls.userState.markAsTouched();
      }
      if (component.types.includes('postal_code')) {
        this.residenceForm.controls.zipCode.setValue(component.shortText ?? '');
        this.residenceForm.controls.zipCode.markAsTouched();
      }
    });
  }

  removeSpaces(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value
      .trim() // Remove leading and trailing spaces
      .replace(/\s{2,}/g, ' ') // Replace multiple spaces with a single space
      .replace(/\.+$/g, ''); // Remove trailing dots
  }
}
